import { AccountInterface, Nullable } from '../interfaces'
import { ImportHeaderListItemDTO } from '../interfaces/Import/ShipmentBulkUploadApi'

/**
 * Pertenencia de una carga masiva a la línea de facturación activa.
 *
 * Un mismo login opera VARIAS líneas (se cambian desde el Header sin volver a
 * loguear), y el backend autoriza `GET /shipment-bulk/{id}` por `creation_user`,
 * no por línea: quien creó la carga con una línea la sigue viendo con otra
 * activa. Estas funciones son la guarda de UI que faltaba.
 *
 * La regla ESPEJA el filtro del listado a propósito, para que todo lo que se ve
 * en `/shipment/bulk-upload/list` se pueda abrir. El backend combina los dos
 * filtros con `elif` (`billing_account` gana si viene), y al crear se manda
 * `billing_account` SOLO si la línea es a crédito (`accountSiteID ≠ 0`) y
 * `account_id` siempre. Es decir:
 *
 * - carga a crédito → guarda `billing_account` (código del sitio) Y `account_id`
 * - carga COD/contado → guarda solo `account_id`
 *
 * Si la guarda fuera más estricta que el filtro, una fila visible en el listado
 * redirigiría al hacer clic.
 */

const normalize = (value?: Nullable<string | number>): string | null => {
  if (value === null || value === undefined) return null
  const text = String(value).trim()
  return text === '' ? null : text
}

/** Copy único del bloqueo, para que el origen y el destino digan lo mismo. */
export const BULK_UPLOAD_DENIED_MESSAGE =
  'No tienes permiso a consultar esa carga masiva, pertenece a otra línea de facturación.'

/**
 * Filtros de dueño para `GET /shipment-bulk/list`.
 *
 * `billing_account` es el CÓDIGO del sitio (`accountCode`), no el `accountSiteID`,
 * y solo aplica a líneas a crédito. Mismo criterio que usa la creación.
 */
export const bulkUploadOwnerFilter = (
  client?: Nullable<AccountInterface>
): { billing_account?: string; account_id?: string } => {
  const hasSite = !!client?.accountSiteID
  const accountCode = normalize(client?.accountCode)
  return {
    billing_account: hasSite && accountCode ? accountCode : undefined,
    account_id: normalize(client?.id) ?? undefined,
  }
}

/**
 * ¿La carga pertenece a la línea de facturación activa?
 *
 * FALLA EN ABIERTO si el header no trae ninguno de los dos campos: el backend
 * hoy exige al menos uno (error `shipping.ShipmentBulkUploadHeader.12`), así que
 * eso solo puede pasar en filas históricas, y bloquearlas las volvería
 * inaccesibles para siempre.
 *
 * Esto es una guarda de UI, NO seguridad: la API sigue siendo llamable directo.
 */
export const ownsBulkUpload = (
  header?: Nullable<
    Pick<ImportHeaderListItemDTO, 'billing_account' | 'account_id'>
  >,
  client?: Nullable<AccountInterface>
): boolean => {
  if (!header) return false

  const billingAccount = normalize(header.billing_account)
  const accountId = normalize(header.account_id)
  if (!billingAccount && !accountId) return true

  if (!client) return false

  // Línea a crédito: tiene que ser exactamente el mismo sitio. No alcanza con
  // compartir el accountID, porque varias líneas cuelgan del mismo.
  const hasSite = !!client.accountSiteID
  const clientCode = normalize(client.accountCode)
  if (hasSite && clientCode) return billingAccount === clientCode

  // COD/contado: se compara la cuenta. El GUID puede llegar en cualquier caja.
  const clientId = normalize(client.id)
  return (
    !!accountId && !!clientId && accountId.toLowerCase() === clientId.toLowerCase()
  )
}
